// CEO Cockpit — the owner's one-screen business read (BI dashboard #1).
// Composes live engine cubes: revenue trend, cash movement, top customers,
// receivables ageing — plus the same KPI strip the Gateway hero uses.
// Every card links to its full engine report (/r/<code>) for drill-down.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { useChaiBrief } from '@/api/reports';
import { Money } from '@/components/primitives/Money';
import { formatINR, formatDateDDMMMYYYY, fyStartIso, monthStartIso, daysAgoIso, todayIso } from '@/utils/format';
import { cn } from '@/lib/cn';

interface RunResponse {
  code: string;
  title: string;
  rows: Array<Record<string, number | string>>;
  totals: Record<string, number>;
  rowCount: number;
}

function useEngine(code: string, from: string, to: string, grain?: string) {
  const grainParam = grain ? `&grain=${grain}` : '';
  return useQuery<RunResponse>({
    queryKey: ['report-engine', code, from, to, grain ?? null],
    queryFn: async () => (await api.get(`/reports-engine/run/${encodeURIComponent(code)}?from=${from}&to=${to}${grainParam}`)).data,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

const MONTH_LABEL = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return `${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m - 1]} ${String(y).slice(-2)}`;
};

const PRESETS = [
  { key: 'mtd', label: 'MTD', from: () => monthStartIso() },
  { key: '90d', label: '90d', from: () => daysAgoIso(89) },
  { key: 'fy', label: 'FY-TD', from: () => fyStartIso() },
];

export function CeoCockpitPage() {
  const [from, setFrom] = useState<string>(fyStartIso());
  const to = todayIso();
  const brief = useChaiBrief();

  // Time reports merged into one `time` dim with a grain param (v2.5). The cockpit
  // wants monthly buckets.
  const revenue = useEngine('sales.time.revenue', from, to, 'month');
  const cash = useEngine('cashbank.time.net', from, to, 'month');
  const topCustomers = useEngine('sales.party.revenue', from, to);
  const ageing = useEngine('receivables.ageBucket.outstanding', from, to);

  // Month cubes arrive sorted by size — the cockpit wants chronology.
  const revenueRows = useMemo(
    () => [...(revenue.data?.rows ?? [])].sort((a, b) => String(a.dimension).localeCompare(String(b.dimension))),
    [revenue.data],
  );
  const cashRows = useMemo(
    () => [...(cash.data?.rows ?? [])].sort((a, b) => String(a.dimension).localeCompare(String(b.dimension))),
    [cash.data],
  );
  const customerRows = (topCustomers.data?.rows ?? []).slice(0, 6);
  const AGE_ORDER = ['0-30 days', '31-60 days', '61-90 days', '90+ days'];
  const ageingRows = useMemo(
    () => [...(ageing.data?.rows ?? [])].sort((a, b) => AGE_ORDER.indexOf(String(a.dimension)) - AGE_ORDER.indexOf(String(b.dimension))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ageing.data],
  );

  const maxRevenue = Math.max(1, ...revenueRows.map((r) => Number(r.revenue) || 0));
  const maxCustomer = Math.max(1, ...customerRows.map((r) => Number(r.revenue) || 0));
  const maxAgeing = Math.max(1, ...ageingRows.map((r) => Math.abs(Number(r.outstanding) || 0)));

  return (
    <AppShell>
      {/* Header */}
      <section className="relative overflow-hidden rounded-lg border border-mist bg-haze p-6 sm:p-8" style={{ backgroundImage: 'var(--wash-hero)' }}>
        <div className="absolute inset-x-0 top-0 h-1 bg-cobalt" aria-hidden />
        <div className="relative flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <a href="/bi" className="text-caption text-cool hover:text-obsidian">← BI Hub</a>
              <span className="inline-flex items-center h-5 px-2 rounded-sm bg-cobalt/10 text-cobalt text-nano font-medium uppercase tracking-[0.12em]">Dashboard</span>
            </div>
            <h1 className="font-display text-display-xl font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
              CEO Cockpit
            </h1>
            <p className="mt-1 text-body-md text-slate">Revenue · cash · outstanding · collections — live from your books, {formatDateDDMMMYYYY(new Date())}.</p>
          </div>
          <div className="flex items-center gap-1 rounded-md border border-mist bg-frost p-1">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setFrom(p.from())}
                className={cn('rounded-sm px-2.5 py-1 text-caption transition', from === p.from() ? 'bg-cobalt text-snow' : 'text-slate hover:bg-frost')}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        <div className="orz-stagger relative mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Kpi label="Cash on hand" value={brief.data?.cashOnHand ?? 0} hint="Cash + bank, incl. openings" />
          <Kpi label="Today's collections" value={brief.data?.todaysCollections ?? 0} hint="Money in today" />
          <Kpi label="Receivables" value={brief.data?.receivables ?? 0} hint="Bill-wise open" />
          <Kpi label="Vouchers today" value={brief.data?.vouchersToday ?? 0} hint="Across all types" count />
        </div>
      </section>

      <div className="orz-stagger mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Revenue by month */}
        <Card title="Revenue by month" total={revenue.data?.totals?.revenue} reportHref="/r/sales.time.revenue" loading={revenue.isLoading}>
          {revenueRows.length === 0 && <Empty label="No sales in this period." />}
          {revenueRows.map((r) => {
            const v = Number(r.revenue) || 0;
            return (
              <BarRow
                key={String(r.dimension)}
                label={MONTH_LABEL(String(r.dimension))}
                sub={`${Number(r.bills) || 0} bills`}
                value={v}
                pct={(v / maxRevenue) * 100}
              />
            );
          })}
        </Card>

        {/* Cash movement by month */}
        <Card title="Cash & bank movement" total={cash.data?.totals?.net} totalLabel="Net" reportHref="/r/cashbank.time.net" loading={cash.isLoading}>
          {cashRows.length === 0 && <Empty label="No cash/bank entries in this period." />}
          {cashRows.map((r) => {
            const inflow = Number(r.inflow) || 0;
            const outflow = Number(r.outflow) || 0;
            const net = Number(r.net) || 0;
            return (
              <div key={String(r.dimension)} className="grid grid-cols-[64px_1fr_1fr_110px] items-center gap-x-3 border-b border-mist/60 py-1.5 text-body-sm last:border-b-0">
                <span className="text-caption text-cool">{MONTH_LABEL(String(r.dimension))}</span>
                <span className="font-mono tabular-nums text-emerald">+{formatINR(inflow, { decimals: 0 })}</span>
                <span className="font-mono tabular-nums text-slate">−{formatINR(outflow, { decimals: 0 })}</span>
                <span className={cn('text-right font-mono tabular-nums font-medium', net >= 0 ? 'text-obsidian' : 'text-crimson')}>
                  {formatINR(net, { decimals: 0 })}
                </span>
              </div>
            );
          })}
        </Card>

        {/* Top customers */}
        <Card title="Top customers" total={topCustomers.data?.totals?.revenue} reportHref="/r/sales.party.revenue" loading={topCustomers.isLoading}>
          {customerRows.length === 0 && <Empty label="No sales in this period." />}
          {customerRows.map((r) => {
            const v = Number(r.revenue) || 0;
            return (
              <BarRow
                key={String(r.dimension)}
                label={String(r.dimension)}
                sub={`${Number(r.bills) || 0} bills`}
                value={v}
                pct={(v / maxCustomer) * 100}
              />
            );
          })}
        </Card>

        {/* Receivables ageing */}
        <Card title="Receivables ageing" total={ageing.data?.totals?.outstanding} reportHref="/r/receivables.ageBucket.outstanding" loading={ageing.isLoading}>
          {ageingRows.length === 0 && <Empty label="No open bills — everything is collected." />}
          {ageingRows.map((r) => {
            const v = Number(r.outstanding) || 0;
            const overdue = String(r.dimension).startsWith('61') || String(r.dimension).startsWith('90');
            return (
              <BarRow
                key={String(r.dimension)}
                label={String(r.dimension)}
                sub={`${Number(r.refs) || 0} bills`}
                value={v}
                pct={(Math.abs(v) / maxAgeing) * 100}
                tone={overdue ? 'crimson' : 'cobalt'}
              />
            );
          })}
        </Card>
      </div>
    </AppShell>
  );
}

function Kpi({ label, value, hint, count }: { label: string; value: number; hint: string; count?: boolean }) {
  return (
    <div className="orz-lift relative overflow-hidden rounded-md border border-mist bg-haze p-4" style={{ backgroundImage: 'var(--wash-card)' }}>
      <div className="text-micro font-medium uppercase tracking-[0.12em] text-cool">{label}</div>
      <div className="mt-2 min-w-0 overflow-hidden text-body-lg sm:text-display-md font-semibold text-obsidian" data-numeric>
        {count
          ? <span className="font-mono tabular-nums">{value.toLocaleString('en-IN')}</span>
          : <Money amount={value} decimals={0} className="whitespace-nowrap" />}
      </div>
      <div className="mt-1 text-caption text-cool">{hint}</div>
    </div>
  );
}

function Card({ title, total, totalLabel, reportHref, loading, children }: {
  title: string; total?: number; totalLabel?: string; reportHref: string; loading?: boolean; children: React.ReactNode;
}) {
  return (
    <section className="orz-lift rounded-md border border-mist bg-haze p-4 sm:p-5">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-display-sm font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>{title}</h2>
        <div className="flex items-baseline gap-3">
          {total != null && (
            <span className="font-mono tabular-nums text-body-sm text-slate">{totalLabel ?? 'Total'}: {formatINR(total, { decimals: 0 })}</span>
          )}
          <a href={reportHref} className="text-caption text-cobalt hover:underline">Full report →</a>
        </div>
      </div>
      {loading
        ? <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-7 animate-pulse rounded-sm bg-frost" />)}</div>
        : children}
    </section>
  );
}

function BarRow({ label, sub, value, pct, tone = 'cobalt' }: { label: string; sub?: string; value: number; pct: number; tone?: 'cobalt' | 'crimson' }) {
  return (
    <div className="border-b border-mist/60 py-1.5 last:border-b-0">
      <div className="flex items-baseline justify-between gap-3 text-body-sm">
        <span className="truncate text-obsidian">{label}</span>
        <span className="shrink-0 font-mono tabular-nums font-medium text-obsidian">{formatINR(value, { decimals: 0 })}</span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-frost">
          <div className={cn('h-full rounded-full', tone === 'crimson' ? 'bg-crimson/40' : 'bg-cobalt/40')} style={{ width: `${Math.max(2, Math.min(100, pct))}%` }} />
        </div>
        {sub && <span className="shrink-0 text-nano text-cool">{sub}</span>}
      </div>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="py-6 text-center text-caption text-cool">{label}</div>;
}
